import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts';
import { getQuestions, submitQuiz } from '../services/api';
import { Question, Language } from '../types/quiz';
import { ProfileResult } from '../types/result';

export const useQuiz = () => {
  const { language } = useLanguage();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ProfileResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuestions(language);
      setQuestions(data.questions);
    } catch (e) {
      setError('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleAnswer = async (optionId: string) => {
    const newAnswers = [...answers, optionId];
    setAnswers(newAnswers);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Last question — submit
      setSubmitting(true);
      try {
        const profileResult = await submitQuiz({
          answers: newAnswers,
          language,
        });
        setResult(profileResult);
      } catch (e) {
        setError('Failed to submit quiz');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const restart = () => {
    setCurrentIndex(0);
    setAnswers([]);
    setResult(null);
    setError(null);
    fetchQuestions();
  };

  const currentQuestion = questions[currentIndex] || null;
  const progress = questions.length > 0 ? ((currentIndex) / questions.length) * 100 : 0;

  return {
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions: questions.length,
    answers,
    loading,
    submitting,
    result,
    error,
    progress,
    handleAnswer,
    restart,
  };
};
